/**
 * The car's SKIN: decals, racing numbers and country flags, drawn into the
 * body's own UV space.
 *
 * THERE ARE NO ASSET FILES and none are being added, so every mark on a car is
 * arithmetic. This file owns that arithmetic and nothing else: it produces RGBA
 * bytes and never touches WebGL, never touches a canvas, and never calls
 * `Math.random`. ./livery uploads what comes out of here; ./materials does the
 * same trick for the flake and the shut lines.
 *
 * The generator names are the CATALOGUE's — `flames`, `flagNordicCross`,
 * `numberRoundel` — and the parameter names are the ones src/game/items.ts
 * writes. That is the contract src/game/cosmetics.ts describes: an item is a
 * recipe, and this is the kitchen. A recipe naming a generator that is not in
 * one of the tables below is a build failure, not an invisible car; see
 * `missingGenerators` in ./livery.
 *
 * ===========================================================================
 * SKIN SPACE
 * ===========================================================================
 *
 * `buildBodyGeometry` in ./car-body emits one UV per lofted vertex:
 *
 *     u = j / SECTION_POINTS     j = index around the closed cross-section
 *     v = (nose - x) / span      0 at the nose, 1 at the tail
 *
 * A closed section runs centre-bottom -> up the RIGHT flank -> centre-top ->
 * down the LEFT flank, so u = 0.5 is the crown centreline and the two halves of
 * the car are mirror images in u. Drawing in raw u is therefore drawing in a
 * coordinate that means "right side" for half its range and "left side,
 * backwards" for the other half — which is exactly how you get a decal that is
 * fine on one flank and inside out on the other.
 *
 * So everything here is authored in SKIN SPACE instead:
 *
 *     t = v                      0 nose, 1 tail
 *     s = 2 * min(u, 1 - u)      0 underbody centreline, 1 crown centreline
 *     side = u < 0.5 ? right : left
 *
 * `s` is shared by the two flanks, which is what makes a livery symmetric for
 * free. Numbers and flags are the two things that must NOT be mirrored — a
 * reversed 7 is worse than no 7 — so they are drawn per side with the handedness
 * corrected; see `skinTextureData`.
 *
 * ===========================================================================
 * ARC LENGTH
 * ===========================================================================
 *
 * `s` is an INDEX fraction, not a distance: the ten samples up the flank and the
 * two rounding the crown cover very different amounts of bodywork, so a band of
 * constant width in `s` is fat on the flank and thin over the roof. Anything
 * that has to be a shape rather than a stripe — a number, a flag — is therefore
 * placed and sized in uu through `SkinMap`, which measures the real half-section
 * once per body and inverts it. Stripes and washes stay in `s`, where the
 * distortion is what you want: a stripe should follow the panel, not the ruler.
 */

import {
  SECTION_INDEX,
  carBodySpec,
  halfSection,
  sampleStation,
  type CarBodyId,
} from './car-body';

// ---------------------------------------------------------------------------
// Small pure maths
// ---------------------------------------------------------------------------

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);

const fract = (v: number): number => v - Math.floor(v);

/** Hermite ease between two edges, clamped. Every soft edge here goes through it. */
export const smoothBand = (edge0: number, edge1: number, x: number): number => {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
};

/**
 * Soft-edged interval test: 1 inside `[lo, hi]`, 0 outside, feathered by `soft`.
 * Used everywhere instead of a bare comparison, because a hard threshold on a
 * 512-pixel texture stretched over a whole flank reads as a staircase.
 */
export const band = (x: number, lo: number, hi: number, soft: number): number =>
  smoothBand(lo - soft, lo + soft, x) * (1 - smoothBand(hi - soft, hi + soft, x));

/**
 * Deterministic value noise. The same hash ./materials uses for the flake, for
 * the same reason: no PRNG state means the same car comes out of every run and
 * a camo pattern can be asserted in a test.
 */
export const skinNoise = (x: number, y: number): number => {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
};

/** Smoothed value noise over a lattice, so blobs have size rather than being static. */
export const skinNoise2 = (x: number, y: number): number => {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  const xf = x - xi;
  const yf = y - yi;
  const u = xf * xf * (3 - 2 * xf);
  const w = yf * yf * (3 - 2 * yf);
  const a = skinNoise(xi, yi);
  const b = skinNoise(xi + 1, yi);
  const c = skinNoise(xi, yi + 1);
  const d = skinNoise(xi + 1, yi + 1);
  return (a + (b - a) * u) * (1 - w) + (c + (d - c) * u) * w;
};

/** Two octaves, for anything that has to look torn rather than wobbly. */
const fbm2 = (x: number, y: number): number =>
  skinNoise2(x, y) * 0.66 + skinNoise2(x * 2.13 + 5.2, y * 2.13 + 1.3) * 0.34;

/** Distance from `p` to the segment `a -> b`, in whatever units they are in. */
export const distanceToSegment = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number
): number => {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 > 1e-12 ? clamp01(((px - ax) * dx + (py - ay) * dy) / len2) : 0;
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
};

// ---------------------------------------------------------------------------
// Skin landmarks
// ---------------------------------------------------------------------------

/**
 * Skin `s` of a section index. `SECTION_INDEX.top` is the crown centreline, so
 * the half-section spans exactly `s = 0 .. 1` and every landmark below is
 * derived rather than measured — move a sample count in ./car-body and these
 * follow.
 */
export const skinOfIndex = (index: number): number => index / SECTION_INDEX.top;

/** The named bands of the skin, in `s`. */
export const SKIN = {
  /** Underbody plate. Never painted; the loft gives it its own material group. */
  floor: skinOfIndex(SECTION_INDEX.floor),
  /** Bottom of the door, where the sill blade runs. */
  rocker: skinOfIndex(SECTION_INDEX.sideStart),
  /** The beltline crease — the widest point, and the top of the flank. */
  belt: skinOfIndex(SECTION_INDEX.shoulder),
  /** Top of the greenhouse: the hood edge forward, the roof rail over the cabin. */
  hood: skinOfIndex(SECTION_INDEX.hood),
  /** The crown centreline. */
  crown: 1,
} as const;

/** Middle of the flank, which is where a door number goes. */
export const SKIN_DOOR = (SKIN.rocker + SKIN.belt) / 2;

/**
 * The real geometry of one body's half-section, measured once so decals can be
 * placed in uu instead of in index fractions.
 *
 * Measured at the midpoint between the axles: that is the door, it is the
 * flattest part of both bodies, and it is where every marking that has to hold
 * its proportions ends up.
 */
export interface SkinMap {
  readonly body: CarBodyId;
  /** uu of car length spanned by `t = 0 .. 1`. */
  readonly lengthPerT: number;
  /** Cumulative arc length, uu, at each half-section index. */
  readonly arcs: readonly number[];
  /** Half-perimeter of the reference section, uu. */
  readonly arcTotal: number;
  /** Arc length from the underbody centreline out to skin coordinate `s`. */
  arcOfS(s: number): number;
  /** The inverse. Clamped at both ends rather than extrapolating. */
  sOfArc(arc: number): number;
}

const skinMapCache = new Map<CarBodyId, SkinMap>();

export const buildSkinMap = (body: CarBodyId): SkinMap => {
  const cached = skinMapCache.get(body);
  if (cached) return cached;

  const spec = carBodySpec(body);
  const refX = (spec.front.mount.x + spec.back.mount.x) / 2;
  const station = sampleStation(spec.stations, refX);
  // The reference section is measured WITHOUT the wheel wells: the arches are a
  // local dent, and letting one move the whole arc-length scale would shift a
  // door number every time `archClearance` was retuned.
  const half = halfSection(station, () => 0);
  const points: (readonly [number, number])[] = [
    [0, station.bottom],
    ...half,
    [0, station.top],
  ];

  const arcs: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    arcs.push(
      arcs[i - 1] + Math.hypot(points[i][0] - points[i - 1][0], points[i][1] - points[i - 1][1])
    );
  }
  const arcTotal = arcs[arcs.length - 1];
  const last = arcs.length - 1;

  const map: SkinMap = {
    body,
    lengthPerT: spec.stations[0].x - spec.stations[spec.stations.length - 1].x,
    arcs,
    arcTotal,
    arcOfS(s: number): number {
      const at = clamp01(s) * last;
      const i = Math.min(last - 1, Math.floor(at));
      return arcs[i] + (arcs[i + 1] - arcs[i]) * (at - i);
    },
    sOfArc(arc: number): number {
      if (arc <= 0) return 0;
      if (arc >= arcTotal) return 1;
      let i = 0;
      while (i < last - 1 && arcs[i + 1] < arc) i++;
      const span = arcs[i + 1] - arcs[i];
      return (i + (span > 1e-9 ? (arc - arcs[i]) / span : 0)) / last;
    },
  };
  skinMapCache.set(body, map);
  return map;
};

// ---------------------------------------------------------------------------
// Parameter readers
// ---------------------------------------------------------------------------

/** Item parameters, exactly as `ItemDef.params` carries them. */
export type SkinParams = Readonly<Record<string, number | string | boolean>>;

export const num = (params: SkinParams | undefined, key: string, fallback: number): number => {
  const v = params?.[key];
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
};

export const bool = (params: SkinParams | undefined, key: string, fallback: boolean): boolean => {
  const v = params?.[key];
  return typeof v === 'boolean' ? v : fallback;
};

export const str = (params: SkinParams | undefined, key: string, fallback: string): string => {
  const v = params?.[key];
  return typeof v === 'string' && v.length > 0 ? v : fallback;
};

/**
 * A colour parameter. The catalogue writes them as packed numbers and uses a
 * NEGATIVE value (or simply omits the key) for "there isn't one", so both come
 * back as `null` rather than as a nonsense colour.
 */
export const colorParam = (params: SkinParams | undefined, key: string): number | null => {
  const v = params?.[key];
  if (typeof v !== 'number' || !Number.isFinite(v) || v < 0) return null;
  return Math.trunc(v) & 0xffffff;
};

const colorOr = (params: SkinParams | undefined, key: string, fallback: number): number =>
  colorParam(params, key) ?? fallback;

// ---------------------------------------------------------------------------
// Decals
// ---------------------------------------------------------------------------

/**
 * What a decal covers one point of skin with.
 *
 * TWO layers, not one, and that is the whole difference between a decal and a
 * stencil: `accent` is the loadout's accent colour and `detail` is a shade of
 * the PRIMARY, so a flame can have a shadow and a stripe can have a pinstripe
 * without either needing a third colour from the player.
 */
export interface DecalSample {
  accent: number;
  detail: number;
}

/** A prepared decal: parameters already read, so the pixel loop reads none. */
export type DecalPainter = (t: number, s: number, out: DecalSample) => void;

export interface Decal {
  sample: DecalPainter;
  /** Colour for the accent layer, or null to take the loadout's accent. */
  accent: number | null;
}

export type DecalGenerator = (params?: SkinParams) => Decal;

/** Feather width for edges authored in `s` and in `t`. One texel at 512. */
const SOFT_S = 0.006;
const SOFT_T = 0.004;

/** Wrap a bare painter, which is what every generator here is. */
const decal = (sample: DecalPainter, accent: number | null = null): Decal => ({ sample, accent });

/**
 * Jittered lattice: the nearest feature point to `(x, y)` on a unit grid whose
 * points are displaced by a hash of their cell.
 *
 * This is how the scattered patterns — splatter, starfield — stay O(9) per pixel
 * instead of O(count). A list of 220 stars tested against 260,000 pixels is
 * fifty-seven million distance computations for a texture nobody looks at
 * closely; a lattice is the same picture for a two-hundredth of the work.
 */
const nearestFeature = (
  x: number,
  y: number,
  seed: number,
  out: { d: number; hash: number }
): void => {
  const xi = Math.floor(x);
  const yi = Math.floor(y);
  let best = Infinity;
  let bestHash = 0;
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const cx = xi + dx;
      const cy = yi + dy;
      const h = skinNoise(cx * 1.7 + seed * 0.013, cy * 2.3 + seed * 0.029);
      const h2 = skinNoise(cx * 3.1 + seed * 0.041, cy * 1.1 + seed * 0.007);
      const d = Math.hypot(x - (cx + h), y - (cy + h2));
      if (d < best) {
        best = d;
        bestHash = h * 0.5 + h2 * 0.5;
      }
    }
  }
  out.d = best;
  out.hash = bestHash;
};

/**
 * The catalogue's decal vocabulary.
 *
 * Every entry is a RECIPE, and the parameters are what make one recipe several
 * items: `stripes` with two fat bands is a rally car and with eight thin ones is
 * a pace car. The parameter names are src/game/items.ts's, exactly.
 */
export const DECAL_GENERATORS: Readonly<Record<string, DecalGenerator>> = {
  /** No decal. Present so "Stock" is an item rather than a null everyone tests for. */
  plain: () =>
    decal((_t, _s, out) => {
      out.accent = 0;
      out.detail = 0;
    }),

  /**
   * Racing stripes over the crown, running the whole length of the car. Centred
   * on the centreline, so they climb the hood, cross the roof and fall down the
   * deck as one continuous set — which is what a bonnet stripe actually does.
   */
  stripes: (params) => {
    const count = Math.max(1, Math.round(num(params, 'count', 2)));
    const width = num(params, 'width', 0.09);
    const gap = num(params, 'gap', 0.06);
    const pin = bool(params, 'pinstripe', true);
    const pitch = width + gap;
    const offsets: number[] = [];
    // An odd count wants a stripe ON the centreline; an even count wants the gap
    // there, and centring the set on zero gives both without a special case.
    for (let i = 0; i < count; i++) offsets.push((i - (count - 1) / 2) * pitch);
    const outer = offsets[offsets.length - 1] + width / 2;
    return decal((_t, s, out) => {
      const d = 1 - s;
      let a = 0;
      for (const offset of offsets) {
        a = Math.max(a, band(d, offset - width / 2, offset + width / 2, SOFT_S));
      }
      out.accent = a;
      // A hairline of the shade either side, which is what stops a wide stripe
      // reading as a change of paint rather than as a stripe laid over paint.
      out.detail = pin ? Math.max(0, band(d, -outer - 0.022, outer + 0.022, SOFT_S) - a) * 0.6 : 0;
    });
  },

  /**
   * A single stripe down the flank, following the beltline. Tapers toward the
   * nose, because a constant-width band on a body that narrows there reads as a
   * sticker rather than as paint.
   */
  beltStripe: (params) => {
    const at = num(params, 'at', SKIN_DOOR + 0.06);
    const width = num(params, 'width', 0.085);
    const taper = num(params, 'taper', 0.55);
    return decal((t, s, out) => {
      const w = (width * (taper + (1 - taper) * smoothBand(0, 0.42, t))) / 2;
      out.accent = band(s, at - w, at + w, SOFT_S);
      out.detail = Math.max(0, band(s, at - w - 0.03, at + w + 0.03, SOFT_S) - out.accent) * 0.8;
    });
  },

  /**
   * Two thin pinstripes along the shoulder line. The cheapest decal there is and
   * the one that reads best at chase distance, because it lands exactly on the
   * crease the loft already breaks its shading along.
   */
  pinstripe: (params) => {
    const at = num(params, 'at', SKIN.belt);
    const width = num(params, 'width', 0.016);
    const spacing = num(params, 'spacing', 0.032);
    return decal((_t, s, out) => {
      out.accent = Math.max(
        band(s, at - spacing - width / 2, at - spacing + width / 2, SOFT_S * 0.6),
        band(s, at + spacing - width / 2, at + spacing + width / 2, SOFT_S * 0.6)
      );
      out.detail = 0;
    });
  },

  /**
   * Flames off the nose. The tongue edge is a sine in `s` plus a noise field,
   * which gives the licking, uneven boundary a single sine does not — and it
   * stays pinned to the front, where a flame has to start.
   */
  flames: (params) => {
    const reach = num(params, 'reach', 0.52);
    const tongues = Math.max(1, num(params, 'tongues', 7));
    const rough = clamp01(num(params, 'roughness', 0.32));
    return decal((t, s, out) => {
      const wave =
        0.5 +
        0.5 * Math.sin(s * tongues * Math.PI * 2) * (1 - rough) +
        rough * (fbm2(s * 11, 3.7) - 0.5) * 2.4;
      const edge = reach * (0.55 + 0.45 * clamp01(wave));
      out.accent = 1 - smoothBand(edge - 0.06, edge + 0.02, t);
      // The soot behind the flame: the reason a two-layer flame reads as heat
      // rather than as an orange sticker.
      out.detail = Math.max(0, (1 - smoothBand(edge + 0.01, edge + 0.1, t)) - out.accent);
    });
  },

  /**
   * Forward-pointing chevrons down the flank. `|s - centre|` against `t` is the
   * whole shape: a V whose apex sweeps backward once per period.
   */
  chevron: (params) => {
    const count = Math.max(1, Math.round(num(params, 'count', 4)));
    const width = num(params, 'width', 0.05);
    const rake = num(params, 'rake', 0.55);
    const centre = num(params, 'centre', SKIN_DOOR);
    // Packed into the middle of the car: chevrons that reach the bumpers read as
    // a wrap rather than as a mark.
    const span = 0.66;
    const period = span / count;
    return decal((t, s, out) => {
      const phase = t - 0.2 + Math.abs(s - centre) * rake;
      if (phase < 0 || phase > span) {
        out.accent = 0;
        out.detail = 0;
        return;
      }
      const f = fract(phase / period);
      out.accent = band(f, 0, width / period, SOFT_T * 2);
      out.detail = band(f, width / period, width / period + 0.05, SOFT_T * 2) * 0.6;
    });
  },

  /** A hexagonal cell grid — technical, and the one pattern that survives being small. */
  hex: (params) => {
    const scale = Math.max(2, num(params, 'scale', 16));
    const line = clamp01(num(params, 'line', 0.14));
    const fade = clamp01(num(params, 'fade', 1));
    return decal((t, s, out) => {
      // Three sheared ridges at 60 degrees: the cheapest hex lattice there is.
      const x = t * scale;
      const y = s * scale * 0.72;
      const edge = Math.min(
        Math.abs(Math.sin((x + y * 0.5) * Math.PI)),
        Math.min(Math.abs(Math.sin((x - y * 0.5) * Math.PI)), Math.abs(Math.sin(y * Math.PI)))
      );
      const cells = 1 - smoothBand(0, Math.max(0.04, line), edge);
      // The dissolve is per CELL, not per pixel, so the pattern breaks up into
      // whole hexagons falling away rather than into fog.
      const id = skinNoise(Math.round(x + y * 0.5), Math.round(y));
      const keep = fade <= 0 ? 1 : smoothBand(0, 1, (t + id * fade - fade * 0.4) * 1.7);
      out.accent = cells * keep;
      out.detail = 0;
    });
  },

  /** Disruptive camouflage: thresholds of one smoothed noise field. */
  camo: (params) => {
    const scale = Math.max(1, num(params, 'scale', 7));
    const coverage = clamp01(num(params, 'coverage', 0.46));
    return decal((t, s, out) => {
      const n = fbm2(t * scale * 2.1, s * scale);
      out.accent = smoothBand(coverage - 0.04, coverage + 0.04, n);
      const m = fbm2(t * scale * 3.3 + 11.3, s * scale * 1.6 + 5.1);
      out.detail = Math.max(0, smoothBand(0.6, 0.68, m) - out.accent);
    });
  },

  /** A sweeping wave along the flank, cresting over the rear arch. */
  wave: (params) => {
    const amplitude = num(params, 'amplitude', 0.14);
    const width = num(params, 'width', 0.16);
    const cycles = num(params, 'cycles', 1.15);
    const centre = num(params, 'centre', SKIN_DOOR);
    return decal((t, s, out) => {
      const line = centre + amplitude * Math.sin((t * cycles - 0.15) * Math.PI * 2);
      out.accent = band(s, line - width / 2, line + width / 2, SOFT_S);
      out.detail = Math.max(
        0,
        band(s, line - width / 2 - 0.035, line + width / 2 + 0.035, SOFT_S) - out.accent
      );
    });
  },

  /** Halftone: a dot grid whose dots grow toward the tail. */
  dots: (params) => {
    const scale = Math.max(2, num(params, 'scale', 22));
    const grow = bool(params, 'grow', true);
    const maxRadius = num(params, 'radius', 0.42);
    return decal((t, s, out) => {
      const x = t * scale;
      const y = s * scale * 0.6;
      const fx = x - Math.floor(x) - 0.5;
      const fy = y - Math.floor(y) - 0.5;
      const r = maxRadius * (grow ? smoothBand(0.1, 0.95, t) : 1);
      out.accent = 1 - smoothBand(Math.max(0, r - 0.08), r, Math.hypot(fx, fy));
      out.detail = 0;
    });
  },

  /** A technical grid: thin lines both ways, heavier every fifth. */
  grid: (params) => {
    const along = Math.max(1, num(params, 'along', 18));
    const around = Math.max(1, num(params, 'around', 10));
    const line = num(params, 'line', 0.055);
    return decal((t, s, out) => {
      const gx = t * along;
      const gy = s * around;
      const dx = Math.abs(gx - Math.round(gx));
      const dy = Math.abs(gy - Math.round(gy));
      const major = Math.round(gx) % 5 === 0 || Math.round(gy) % 5 === 0 ? 1.9 : 1;
      out.accent = Math.max(
        1 - smoothBand(0, line * major, dx),
        1 - smoothBand(0, line * major * 1.4, dy)
      );
      out.detail = 0;
    });
  },

  /** Angular shards breaking off the tail. Straight edges, no curves anywhere. */
  shards: (params) => {
    const count = Math.max(2, Math.round(num(params, 'count', 6)));
    const rake = num(params, 'rake', 1.35);
    const start = num(params, 'start', 0.42);
    return decal((t, s, out) => {
      const phase = (t - start) * count + s * rake;
      const f = fract(phase);
      const reach = smoothBand(start - 0.05, 1, t);
      out.accent = band(f, 0, 0.46, SOFT_T * 2) * reach;
      out.detail = band(f, 0.52, 0.68, SOFT_T * 2) * reach;
    });
  },

  /** Tapered tiger stripes across the flank. Narrow at the belt, wide at the sill. */
  tiger: (params) => {
    const count = Math.max(2, Math.round(num(params, 'count', 9)));
    const rake = num(params, 'rake', 0.28);
    const taper = num(params, 'taper', 0.7);
    return decal((t, s, out) => {
      const phase = t * count + (SKIN.belt - s) * rake * count;
      const f = fract(phase);
      const wobble = 0.06 * Math.sin(s * 11 + t * 5);
      const w =
        0.16 + taper * 0.24 * (1 - clamp01((s - SKIN.rocker) / (SKIN.belt - SKIN.rocker)));
      out.accent = band(f + wobble, 0.5 - w, 0.5 + w, SOFT_T * 3);
      out.detail = 0;
    });
  },

  /**
   * A hard two-tone split across the car. `rake` leans it; at zero it is a
   * straight transverse line, which is the classic half-and-half livery.
   */
  split: (params) => {
    const at = num(params, 'at', 0.5);
    const rake = num(params, 'rake', 0.22);
    const soft = num(params, 'soft', 0.01);
    const stripe = bool(params, 'stripe', true);
    return decal((t, s, out) => {
      const line = at + (s - SKIN_DOOR) * rake;
      out.accent = smoothBand(line - soft, line + soft, t);
      out.detail = stripe ? band(t, line - 0.026, line - 0.006, SOFT_T) : 0;
    });
  },

  /**
   * A wrap covering the whole lower body up to the beltline. The simplest
   * two-tone there is, and the one that most changes a car's read from a chase
   * camera, because it is the half of the body that is never in shadow.
   */
  lowerWrap: (params) => {
    const at = num(params, 'at', SKIN.belt);
    const dip = num(params, 'dip', 0.06);
    return decal((t, s, out) => {
      const line = at - dip * Math.sin(t * Math.PI);
      out.accent = 1 - smoothBand(line - 0.012, line + 0.012, s);
      out.detail = band(s, line - 0.004, line + 0.022, SOFT_S);
    });
  },

  // -------------------------------------------------------------------------
  // Beyond the catalogue
  // -------------------------------------------------------------------------
  //
  // Implemented and tested, and not currently named by any item. They are here
  // because a renderer that can only draw what has already been asked for makes
  // every new item a render change; the catalogue can reach for any of these
  // without touching this file. The coverage test runs the other way round —
  // every item's generator must EXIST — so extra vocabulary costs nothing.

  /** Ink splatter: jittered blobs with a ragged edge and a halo of thinner ink. */
  splatter: (params) => {
    const blobs = Math.max(2, Math.round(num(params, 'blobs', 14)));
    const variance = clamp01(num(params, 'sizeVariance', 0.6));
    const seed = num(params, 'patternSeed', 1337);
    const scale = Math.sqrt(blobs) * 1.35;
    const hit = { d: 0, hash: 0 };
    return decal((t, s, out) => {
      nearestFeature(t * scale, s * scale, seed, hit);
      const radius = 0.34 * (1 - variance * 0.7 + variance * 1.4 * hit.hash);
      const ragged = radius * (1 + 0.28 * (fbm2(t * 34 + seed, s * 34) - 0.5));
      out.accent = 1 - smoothBand(ragged * 0.82, ragged, hit.d);
      out.detail = Math.max(0, (1 - smoothBand(ragged, ragged * 1.5, hit.d)) - out.accent) * 0.7;
    });
  },

  /** A chequerboard. `rotation` turns it. */
  checker: (params) => {
    const cells = Math.max(2, Math.round(num(params, 'cells', 10)));
    const angle = (num(params, 'rotation', 0) * Math.PI) / 180;
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    return decal((t, s, out) => {
      const x = (t * ca - s * sa) * cells;
      const y = (t * sa + s * ca) * cells;
      out.accent = (Math.floor(x) + Math.floor(y)) % 2 !== 0 ? 1 : 0;
      out.detail = 0;
    });
  },

  /** A gradient along the car, optionally quantized into hard bands. */
  gradient: (params) => {
    const stops = Math.max(2, Math.round(num(params, 'stops', 2)));
    const angle = (num(params, 'angle', 0) * Math.PI) / 180;
    const ca = Math.cos(angle);
    const sa = Math.sin(angle);
    return decal((t, s, out) => {
      const p = clamp01(0.5 + (t - 0.5) * ca + (s - 0.5) * sa);
      out.accent = stops <= 2 ? p : Math.round(p * (stops - 1)) / (stops - 1);
      out.detail = 0;
    });
  },

  /** Tribal work: curling arms sweeping back from the front arch. */
  tribal: (params) => {
    const arms = Math.max(1, Math.round(num(params, 'arms', 5)));
    const curl = num(params, 'curl', 0.7);
    return decal((t, s, out) => {
      let a = 0;
      for (let i = 0; i < arms; i++) {
        const phase = i / arms;
        const start = 0.12 + phase * 0.22;
        if (t < start) continue;
        const run = clamp01((t - start) / 0.62);
        const line =
          SKIN_DOOR + (phase - 0.5) * 0.34 + Math.sin(run * Math.PI * (0.6 + curl)) * 0.16 * curl * run;
        // Fat at the root, gone at the tip. That taper is the whole style.
        const w = 0.034 * (1 - run) * (1 - run) + 0.004;
        a = Math.max(a, band(s, line - w, line + w, SOFT_S));
      }
      out.accent = a;
      out.detail = 0;
    });
  },

  /**
   * Circuit traces: a Manhattan lattice where each cell independently carries a
   * horizontal run, a vertical run, or neither, with a via where the two meet.
   */
  circuit: (params) => {
    const trace = Math.max(0.002, num(params, 'traceWidth', 0.012));
    const density = clamp01(num(params, 'density', 0.5));
    const via = Math.max(trace, num(params, 'viaSize', 0.02));
    const scale = 1 / 0.055;
    return decal((t, s, out) => {
      const x = t * scale;
      const y = s * scale;
      const cx = Math.floor(x);
      const cy = Math.floor(y);
      const fx = x - cx - 0.5;
      const fy = y - cy - 0.5;
      const hasH = skinNoise(cx, cy) < density;
      const hasV = skinNoise(cx + 31.4, cy + 17.1) < density;
      const w = (trace * scale) / 2;
      let a = 0;
      if (hasH) a = Math.max(a, 1 - smoothBand(w * 0.7, w, Math.abs(fy)));
      if (hasV) a = Math.max(a, 1 - smoothBand(w * 0.7, w, Math.abs(fx)));
      const pad =
        hasH && hasV
          ? 1 - smoothBand((via * scale) / 2.6, (via * scale) / 2, Math.hypot(fx, fy))
          : 0;
      out.accent = Math.max(a, pad);
      out.detail = Math.max(0, (skinNoise(cx + 5.5, cy + 9.9) < density * 0.5 ? 0.5 : 0) - out.accent);
    });
  },

  /** Overlapping scales, laid in offset rows down the flank. */
  scales: (params) => {
    const rows = Math.max(3, Math.round(num(params, 'rows', 18)));
    const overlap = clamp01(num(params, 'overlap', 0.35));
    const cols = Math.max(4, Math.round(rows * 1.6));
    return decal((t, s, out) => {
      const y = s * rows;
      const row = Math.floor(y);
      // Every other row offsets by half a scale, which is what makes them nest.
      const x = t * cols + (row % 2 === 0 ? 0 : 0.5);
      const fx = x - Math.floor(x) - 0.5;
      const fy = y - row;
      const d = Math.hypot(fx, (fy - 1 + overlap) * 0.9);
      const edge = 0.5 + overlap * 0.25;
      out.accent = 1 - smoothBand(edge - 0.16, edge - 0.05, d);
      out.detail = band(d, edge - 0.05, edge, 0.02);
    });
  },

  /** Jagged bolts running the length of the car, each with its own glow. */
  lightning: (params) => {
    const bolts = Math.max(1, Math.round(num(params, 'bolts', 4)));
    const jitter = num(params, 'jitter', 0.4);
    return decal((t, s, out) => {
      let a = 0;
      let glow = 0;
      for (let i = 0; i < bolts; i++) {
        const seed = i * 13.7;
        const zig =
          (fract(Math.sin((t * 9 + seed) * 12.9898) * 43758.5453) - 0.5) * 0.2 +
          (skinNoise2(t * 22, seed) - 0.5) * 0.16 +
          (skinNoise2(t * 5, seed + 3.3) - 0.5) * 0.3;
        const line = SKIN_DOOR + ((i + 0.5) / bolts - 0.5) * 0.5 + zig * jitter * 1.6;
        const d = Math.abs(s - line);
        a = Math.max(a, 1 - smoothBand(0.006, 0.014, d));
        glow = Math.max(glow, 1 - smoothBand(0.014, 0.06, d));
      }
      out.accent = a;
      out.detail = Math.max(0, glow - a) * 0.8;
    });
  },

  /** A star field: bright points over a nebula wash. */
  starfield: (params) => {
    const stars = Math.max(10, Math.round(num(params, 'stars', 220)));
    const twinkle = clamp01(num(params, 'twinkle', 0.3));
    const scale = Math.sqrt(stars) * 0.9;
    const hit = { d: 0, hash: 0 };
    return decal((t, s, out) => {
      nearestFeature(t * scale, s * scale, 7, hit);
      // Size varies per star; `twinkle` widens that spread rather than animating,
      // because a texture cannot animate and pretending otherwise is a lie.
      const r = 0.06 + hit.hash * (0.05 + twinkle * 0.18);
      out.accent = 1 - smoothBand(r * 0.5, r, hit.d);
      out.detail = clamp01(fbm2(t * 3.1, s * 2.2) * 1.4 - 0.45);
    });
  },
};


export const DECAL_GENERATOR_NAMES: readonly string[] = Object.freeze(
  Object.keys(DECAL_GENERATORS).sort()
);

// ---------------------------------------------------------------------------
// Digits
// ---------------------------------------------------------------------------

/**
 * A stroked digit font, authored as polylines in a unit box with y UP.
 *
 * Stroked rather than filled because the rasterizer is then one
 * distance-to-segment test and needs no scanline fill, no winding rule and no
 * curve flattening — and because a racing number IS a stroke: a heavy pen, flat
 * ends, no serifs. Zero carries no slash; a slashed zero on the side of a car
 * looks like a typo rather than like a number.
 */
export const DIGIT_STROKES: readonly (readonly (readonly (readonly [number, number])[])[])[] = [
  // 0
  [[[0.5, 1], [0.86, 0.84], [1, 0.5], [0.86, 0.16], [0.5, 0], [0.14, 0.16], [0, 0.5], [0.14, 0.84], [0.5, 1]]],
  // 1
  [[[0.06, 0.76], [0.46, 1], [0.46, 0]], [[0.06, 0], [0.9, 0]]],
  // 2
  [[[0.02, 0.78], [0.24, 0.98], [0.74, 0.98], [0.96, 0.76], [0.9, 0.5], [0.04, 0.03], [1, 0.03]]],
  // 3
  [
    [[0.04, 0.82], [0.28, 1], [0.74, 1], [0.96, 0.82], [0.74, 0.56]],
    [[0.4, 0.56], [0.78, 0.56], [0.97, 0.3], [0.74, 0.02], [0.26, 0.02], [0.03, 0.18]],
  ],
  // 4
  [[[0.74, 0], [0.74, 1], [0.02, 0.28], [1, 0.28]]],
  // 5
  [[[0.94, 1], [0.16, 1], [0.1, 0.6], [0.58, 0.66], [0.94, 0.46], [0.86, 0.12], [0.52, 0], [0.12, 0.06]]],
  // 6
  [[[0.86, 0.94], [0.5, 1], [0.14, 0.76], [0.06, 0.36], [0.3, 0.02], [0.72, 0.02], [0.94, 0.3], [0.72, 0.6], [0.3, 0.58], [0.1, 0.4]]],
  // 7
  [[[0.02, 1], [1, 1], [0.4, 0]]],
  // 8
  [
    [[0.5, 1], [0.84, 0.86], [0.82, 0.64], [0.5, 0.53], [0.18, 0.64], [0.16, 0.86], [0.5, 1]],
    [[0.5, 0.53], [0.88, 0.37], [0.86, 0.13], [0.5, 0], [0.14, 0.13], [0.12, 0.37], [0.5, 0.53]],
  ],
  // 9
  [[[0.14, 0.06], [0.5, 0], [0.86, 0.24], [0.94, 0.64], [0.7, 0.98], [0.28, 0.98], [0.06, 0.7], [0.28, 0.4], [0.7, 0.42], [0.9, 0.6]]],
];

/** Advance width of one digit, as a fraction of its box, plus the gap after it. */
const DIGIT_ADVANCE = 1.22;

/**
 * Coverage of a digit string at `(x, y)` in a box whose left edge is the first
 * digit's and whose right edge is the last digit's, with y = 0 on the baseline.
 *
 * Returns stroke coverage, so a caller can render it solid, outlined or knocked
 * out of a roundel without this function knowing which.
 */
export const digitsCoverage = (
  digits: string,
  x: number,
  y: number,
  weight: number,
  feather: number
): number => {
  const count = digits.length;
  if (count === 0) return 0;
  const total = count * DIGIT_ADVANCE - (DIGIT_ADVANCE - 1);
  const gx = x * total;
  let best = Infinity;
  for (let i = 0; i < count; i++) {
    const code = digits.charCodeAt(i) - 48;
    if (code < 0 || code > 9) continue;
    const lx = gx - i * DIGIT_ADVANCE;
    // Cheap reject: nothing outside the digit's own box plus the pen radius.
    if (lx < -weight - feather || lx > 1 + weight + feather) continue;
    for (const stroke of DIGIT_STROKES[code]) {
      for (let k = 1; k < stroke.length; k++) {
        const d = distanceToSegment(
          lx,
          y,
          stroke[k - 1][0],
          stroke[k - 1][1],
          stroke[k][0],
          stroke[k][1]
        );
        if (d < best) best = d;
      }
    }
  }
  if (!Number.isFinite(best)) return 0;
  return 1 - smoothBand(weight - feather, weight + feather, best);
};

/** Sanitize a number parameter into one to three digits. */
export const numberDigits = (value: string | number): string => {
  const raw = typeof value === 'number' ? String(Math.trunc(Math.abs(value))) : value;
  const digits = raw.replace(/[^0-9]/g, '').slice(0, 3);
  return digits.length > 0 ? digits : '0';
};

/**
 * The digit string an item asks for. `digits` wins where it is present, because
 * it is the only one of the two that can carry a leading zero — and "07" is a
 * different racing number from "7".
 */
export const numberDigitsOf = (params: SkinParams | undefined): string => {
  const explicit = params?.digits;
  if (typeof explicit === 'string' || typeof explicit === 'number') return numberDigits(explicit);
  return numberDigits(num(params, 'value', 0));
};

// ---------------------------------------------------------------------------
// Panels: numbers and flags
// ---------------------------------------------------------------------------

/**
 * A prepared panel: fills `out` with a packed colour and a coverage, given
 * `(x, y)` in 0..1 across the panel with y UP.
 */
export interface PanelSample {
  /** Packed 0xRRGGBB. Only meaningful where `coverage > 0`. */
  color: number;
  coverage: number;
}

export type PanelPainter = (x: number, y: number, out: PanelSample) => void;

export interface NumberColors {
  /** The digits themselves. */
  ink: number;
  /** Outline or plate, where the style has one. */
  trim: number;
}

export type NumberGenerator = (
  digits: string,
  colors: NumberColors,
  params: SkinParams | undefined
) => PanelPainter;

/**
 * The number styles.
 *
 * The panel is always the same size on the car; what changes is whether the
 * digits are bare, outlined, or knocked out of a roundel. All three are the same
 * `digitsCoverage` call with a different composite, which is what keeps a fourth
 * style to four lines rather than to a second font.
 */
/**
 * Sample the digit string so it FILLS a given fraction of the panel.
 *
 * Two scales, not one, because the panel is 3:2 and the glyph box is square:
 * dividing both axes by the same number would set a two-digit number half as
 * tall as it should be. `fill` is the fraction of the panel HEIGHT the digits
 * take, and the horizontal scale follows from the string's own advance width so
 * the letterforms keep their proportions whatever the panel is.
 */
const fitted = (
  digits: string,
  x: number,
  y: number,
  maxHeight: number,
  maxWidth: number,
  aspect: number,
  weight: number,
  feather: number
): number => {
  // The string's own advance width, in glyph boxes. Three digits are 3.44 boxes
  // wide and one is 1, so the same call sets both without a per-length table.
  const advance = digits.length * DIGIT_ADVANCE - (DIGIT_ADVANCE - 1);
  // Whichever bound binds first wins, and the OTHER axis follows from it, so
  // the letterforms are never stretched — a squeezed 3 is worse than a small one.
  const wide = Math.min(maxWidth, (maxHeight * advance) / aspect);
  const tall = (wide * aspect) / advance;
  return digitsCoverage(digits, (x - 0.5) / wide + 0.5, (y - 0.5) / tall + 0.5, weight, feather);
};

/** Panel aspect: width over height. `NUMBER_PANEL` in ./livery is 22 x 15. */
const PANEL_ASPECT = 22 / 15;

export const NUMBER_GENERATORS: Readonly<Record<string, NumberGenerator>> = {
  numberPlain: (digits, colors, params) => {
    const weight = num(params, 'weight', 0.13);
    return (x, y, out) => {
      out.color = colors.ink;
      out.coverage = fitted(digits, x, y, 0.86, 0.92, PANEL_ASPECT, weight, 0.02);
    };
  },

  numberOutline: (digits, colors, params) => {
    const weight = num(params, 'weight', 0.115);
    const edge = num(params, 'edge', 0.055);
    return (x, y, out) => {
      const inner = fitted(digits, x, y, 0.78, 0.86, PANEL_ASPECT, weight, 0.018);
      const outer = fitted(digits, x, y, 0.78, 0.86, PANEL_ASPECT, weight + edge, 0.018);
      out.color = inner > 0.5 ? colors.ink : colors.trim;
      out.coverage = Math.max(inner, outer);
    };
  },

  /**
   * Digits knocked out of a filled roundel — the classic competition number.
   * The disc is squashed to the panel, so it is an ellipse in panel space and a
   * circle on the car.
   */
  numberRoundel: (digits, colors, params) => {
    const weight = num(params, 'weight', 0.15);
    // INSCRIBED in the panel's short axis, not fitted to its diagonal: a disc
    // that reaches the corners of a 3:2 panel is most of a door, and a door-sized
    // white blob is the first thing anyone notices about a car wearing it.
    const inset = num(params, 'inset', 0.03);
    return (x, y, out) => {
      const dx = (x - 0.5) * PANEL_ASPECT;
      const dy = y - 0.5;
      const radius = 0.5 - inset;
      out.coverage = 1 - smoothBand(radius - 0.012, radius, Math.hypot(dx, dy));
      // The digits fit the DISC, not the panel: the disc is only 0.6 of the
      // panel wide, and a number set to the panel is a number with its ends cut
      // off by its own roundel.
      const across = ((radius * 2) / PANEL_ASPECT) * 0.86;
      out.color =
        fitted(digits, x, y, radius * 1.2, across, PANEL_ASPECT, weight, 0.02) > 0.5
          ? colors.trim
          : colors.ink;
    };
  },
};

export const NUMBER_GENERATOR_NAMES: readonly string[] = Object.freeze(
  Object.keys(NUMBER_GENERATORS).sort()
);

// ---------------------------------------------------------------------------
// Flags
// ---------------------------------------------------------------------------

/**
 * Country flags, as HONEST GEOMETRY.
 *
 * The rule the catalogue is held to — and this is the renderer's half of it — is
 * that a flag goes in only if it is exactly drawable from rectangles, circles
 * and regular polygons. Every generator below is one of those. Nothing here
 * approximates a coat of arms, a ring of stars or a maple leaf: a flag drawn
 * wrong is worse than a flag not offered, because it is wrong about a real
 * country at a size where nobody can tell it was meant to be something else.
 *
 * If the catalogue ever wants a flag none of these can draw, the right answer is
 * to say so rather than to add a generator that smears it.
 *
 * `x` runs hoist -> fly and `y` runs bottom -> top, so a flag's "top band" is at
 * HIGH y.
 */
export type FlagGenerator = (params: SkinParams | undefined) => PanelPainter;

export const FLAG_GENERATORS: Readonly<Record<string, FlagGenerator>> = {
  /**
   * Two or three bands, vertical or horizontal, with an optional unequal split.
   * France, Italy, Ireland, Belgium, Romania, Germany, Netherlands, Austria,
   * Hungary, Poland, Ukraine, Russia, Bulgaria, Estonia, Colombia.
   */
  flagBands: (params) => {
    const count = Math.min(3, Math.max(2, Math.round(num(params, 'count', 3))));
    const vertical = bool(params, 'vertical', true);
    const colors = [
      colorOr(params, 'a', 0x1b3fae),
      colorOr(params, 'b', 0xf3f4f7),
      colorOr(params, 'c', 0xd7263d),
    ];
    // Unequal bands are common (Colombia, Bolivia); one number covers them.
    const first = num(params, 'first', 1 / count);
    const rest = count > 1 ? (1 - first) / (count - 1) : 1;
    return (x, y, out) => {
      // A vertical flag's first band is at the HOIST; a horizontal one's is at
      // the TOP, and y runs upward, so it is measured from 1.
      const p = vertical ? x : 1 - y;
      out.color = p < first ? colors[0] : p < first + rest ? colors[1] : colors[count - 1];
      out.coverage = 1;
    };
  },

  /**
   * The Nordic cross: a cross whose vertical bar is offset toward the hoist.
   * Denmark, Sweden, Finland — and, with the second narrower cross `inner`
   * draws, Norway and Iceland.
   */
  flagNordicCross: (params) => {
    const field = colorOr(params, 'field', 0xc8102e);
    const cross = colorOr(params, 'cross', 0xf3f4f7);
    const inner = colorParam(params, 'inner');
    // With no inner cross the single bar is narrower; with one, the outer bar
    // has to be wide enough to still read as a border around it.
    const width = num(params, 'width', inner === null ? 0.2 : 0.3);
    const innerWidth = num(params, 'innerWidth', 0.14);
    const offset = num(params, 'offset', 0.36);
    return (x, y, out) => {
      const onOuter = Math.abs(x - offset) < width / 2 || Math.abs(y - 0.5) < width / 2;
      const onInner =
        inner !== null &&
        (Math.abs(x - offset) < innerWidth / 2 || Math.abs(y - 0.5) < innerWidth / 2);
      // The item names the CROSS and the border; `inner` is the border, so the
      // narrow bar keeps the cross colour and the wide one takes the outline.
      out.color = onInner ? cross : onOuter ? (inner ?? cross) : field;
      out.coverage = 1;
    };
  },

  /** A centred cross. England, and Switzerland with `reach` shortening the arms. */
  flagCross: (params) => {
    const field = colorOr(params, 'field', 0xd7263d);
    const cross = colorOr(params, 'cross', 0xf3f4f7);
    const width = num(params, 'width', 0.22);
    const reach = num(params, 'reach', 1);
    return (x, y, out) => {
      const vertical = Math.abs(x - 0.5) < width / 2 && Math.abs(y - 0.5) < reach / 2;
      const horizontal = Math.abs(y - 0.5) < width / 2 && Math.abs(x - 0.5) < reach / 2;
      out.color = vertical || horizontal ? cross : field;
      out.coverage = 1;
    };
  },

  /** A saltire — a diagonal cross. Scotland. */
  flagSaltire: (params) => {
    const field = colorOr(params, 'field', 0x0b4ea2);
    const cross = colorOr(params, 'cross', 0xf3f4f7);
    const width = num(params, 'width', 0.2);
    return (x, y, out) => {
      // Perpendicular distance to each diagonal of the unit panel.
      const a = Math.abs(y - x) * Math.SQRT1_2;
      const b = Math.abs(y - (1 - x)) * Math.SQRT1_2;
      out.color = Math.min(a, b) < width / 2 ? cross : field;
      out.coverage = 1;
    };
  },

  /** A disc on a plain field. Japan, Bangladesh, Palau. */
  flagDisc: (params) => {
    const field = colorOr(params, 'field', 0xf3f4f7);
    const disc = colorOr(params, 'disc', 0xbc002d);
    const radius = num(params, 'radius', 0.3);
    const offsetX = num(params, 'offsetX', 0);
    const offsetY = num(params, 'offsetY', 0);
    return (x, y, out) => {
      out.color = Math.hypot(x - 0.5 - offsetX, y - 0.5 - offsetY) < radius ? disc : field;
      out.coverage = 1;
    };
  },

  /**
   * Two bands with a triangle at the hoist. Czechia, the Philippines' geometry,
   * Jordan without its star, Sudan, Kuwait, the UAE.
   */
  flagHoistTriangle: (params) => {
    const a = colorOr(params, 'a', 0xf3f4f7);
    const b = colorOr(params, 'b', 0xd7263d);
    const triangle = colorOr(params, 'triangle', 0x11457e);
    const reach = num(params, 'reach', 0.5);
    return (x, y, out) => {
      out.color = x < reach * (1 - Math.abs(y - 0.5) * 2) ? triangle : y > 0.5 ? a : b;
      out.coverage = 1;
    };
  },

  /**
   * The Union Flag. Included because it IS geometry — a white saltire, a red
   * saltire counterchanged within it, and a fimbriated red cross over both — and
   * because leaving it out while offering France would be an odd place to stop.
   *
   * The COUNTERCHANGE is the part usually got wrong, and it is drawn properly
   * here: the red diagonal sits in the half of the white one nearer the top on
   * the hoist side and nearer the bottom on the fly side, which is the whole
   * reason the flag has a right way up at all.
   */
  flagUnionJack: () => {
    const blue = 0x012169;
    const white = 0xf3f4f7;
    const red = 0xc8102e;
    return (x, y, out) => {
      const cx = x - 0.5;
      const cy = y - 0.5;
      out.coverage = 1;

      // St George, over everything: a red cross with a white fimbriation.
      if (Math.abs(cx) < 0.1 || Math.abs(cy) < 0.1) {
        out.color = Math.abs(cx) < 0.062 || Math.abs(cy) < 0.062 ? red : white;
        return;
      }

      // Signed distance to each diagonal. The sign is the counterchange.
      const d1 = (cy - cx) * Math.SQRT1_2;
      const d2 = (cy + cx) * Math.SQRT1_2;
      const near1 = Math.abs(d1) < 0.105;
      const near2 = Math.abs(d2) < 0.105;
      if (near1 || near2) {
        const useFirst = near1 && (!near2 || Math.abs(d1) < Math.abs(d2));
        const d = useFirst ? d1 : d2;
        const towardHoist = (useFirst ? cx : -cx) < 0;
        const inRed = towardHoist ? d > 0 && d < 0.068 : d < 0 && d > -0.068;
        out.color = inRed ? red : white;
        return;
      }
      out.color = blue;
    };
  },
};

export const FLAG_GENERATOR_NAMES: readonly string[] = Object.freeze(
  Object.keys(FLAG_GENERATORS).sort()
);

// ---------------------------------------------------------------------------
// Composition
// ---------------------------------------------------------------------------

/** A panel to stamp on both flanks: where it sits and how big it is, in uu. */
export interface SkinPanel {
  paint: PanelPainter;
  /** Centre along the car, `t`. */
  t: number;
  /** Centre around the section, in ARC LENGTH from the underbody centreline, uu. */
  arc: number;
  /** Panel size on the car, uu. */
  width: number;
  height: number;
}

/** Everything that goes into one body texture. */
export interface SkinRecipe {
  body: CarBodyId;
  primary: number;
  accent: number;
  decal: Decal | null;
  panels: readonly SkinPanel[];
}

const rgb = (hexColor: number): [number, number, number] => [
  (hexColor >> 16) & 0xff,
  (hexColor >> 8) & 0xff,
  hexColor & 0xff,
];

/** Scale a packed colour toward black (`k < 1`) or white (`k > 1`), per channel. */
export const shadeHex = (hexColor: number, k: number): number => {
  const [r, g, b] = rgb(hexColor);
  const f = (c: number): number =>
    Math.round(Math.max(0, Math.min(255, k <= 1 ? c * k : c + (255 - c) * (k - 1))));
  return (f(r) << 16) | (f(g) << 8) | f(b);
};

/**
 * Perceptual luminance, 0..1, of a packed colour. Used to decide whether a mark
 * needs a dark or a light companion — the reason Titanium White does not vanish
 * into a white number and black does not vanish into its own shadow.
 */
export const luminance = (hexColor: number): number => {
  const [r, g, b] = rgb(hexColor);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
};

/**
 * The body texture, as RGBA bytes.
 *
 * Pure and deterministic: no canvas, no PRNG, no clock. Runs in Node, which is
 * what lets a test assert that a decal actually landed on the flank and that a
 * number is the right way round on both sides of the car.
 *
 * ABSOLUTE COLOUR, not a mask. The material's own `color` stays white and this
 * map carries the paint, which is the only way a decal can be a different colour
 * from the body without a second material and a second draw call.
 */
export const skinTextureData = (recipe: SkinRecipe, size = 512): Uint8Array => {
  const width = size;
  const height = size;
  const data = new Uint8Array(width * height * 4);
  const map = buildSkinMap(recipe.body);

  const [pr, pg, pb] = rgb(recipe.primary);
  const [ar, ag, ab] = rgb(recipe.decal?.accent ?? recipe.accent);
  // The detail layer is a shade of the PRIMARY, chosen by its own brightness so
  // it reads on white paint as well as on black.
  const detailHex = shadeHex(recipe.primary, luminance(recipe.primary) > 0.55 ? 0.66 : 1.5);
  const [dr, dg, db] = rgb(detailHex);

  const sample: DecalSample = { accent: 0, detail: 0 };
  const panelOut: PanelSample = { color: 0, coverage: 0 };
  const paint = recipe.decal?.sample ?? null;

  // Panels are resolved into skin ranges once, not per pixel.
  const placed = recipe.panels.map((panel) => {
    const halfT = panel.width / 2 / map.lengthPerT;
    return {
      paint: panel.paint,
      t0: panel.t - halfT,
      t1: panel.t + halfT,
      s0: map.sOfArc(panel.arc - panel.height / 2),
      s1: map.sOfArc(panel.arc + panel.height / 2),
      arc0: panel.arc - panel.height / 2,
      arc1: panel.arc + panel.height / 2,
    };
  });

  for (let y = 0; y < height; y++) {
    const t = (y + 0.5) / height;
    for (let x = 0; x < width; x++) {
      const u = (x + 0.5) / width;
      const right = u < 0.5;
      const s = 2 * (right ? u : 1 - u);

      let r = pr;
      let g = pg;
      let b = pb;

      if (paint) {
        sample.accent = 0;
        sample.detail = 0;
        paint(t, s, sample);
        const d = clamp01(sample.detail);
        if (d > 0) {
          r += (dr - r) * d;
          g += (dg - g) * d;
          b += (db - b) * d;
        }
        const a = clamp01(sample.accent);
        if (a > 0) {
          r += (ar - r) * a;
          g += (ag - g) * a;
          b += (ab - b) * a;
        }
      }

      for (const panel of placed) {
        if (t < panel.t0 || t > panel.t1 || s < panel.s0 || s > panel.s1) continue;
        // Along the car, and this is the one line that has to be got right or
        // every number on one side of the car is written backwards.
        //
        // The mesh frame is +X = the car's RIGHT and -Z = forward. A viewer
        // looking at the car's right flank is therefore standing on +X looking
        // back along -X, and their screen-right works out as +Z — which is
        // BACKWARD. So on the right flank the nose is on the viewer's right and
        // `t` (nose -> tail) runs right to left; the panel has to be flipped.
        // On the left flank the whole thing mirrors and it does not.
        const along = (t - panel.t0) / (panel.t1 - panel.t0);
        const px = right ? 1 - along : along;
        // Around the section: in ARC LENGTH, so the panel keeps its proportions
        // wherever on the flank it is placed.
        const py = (map.arcOfS(s) - panel.arc0) / (panel.arc1 - panel.arc0);
        if (py < 0 || py > 1) continue;
        panelOut.coverage = 0;
        panelOut.color = 0;
        panel.paint(px, py, panelOut);
        const c = clamp01(panelOut.coverage);
        if (c <= 0) continue;
        const [qr, qg, qb] = rgb(panelOut.color);
        r += (qr - r) * c;
        g += (qg - g) * c;
        b += (qb - b) * c;
      }

      const o = (y * width + x) * 4;
      data[o] = Math.round(r);
      data[o + 1] = Math.round(g);
      data[o + 2] = Math.round(b);
      data[o + 3] = 255;
    }
  }

  return data;
};
